import { Box, Button, MenuItem, TextField, Typography } from "@mui/material";
import { COLORS } from "assets";
import { incidentsApi, monthKey, visibleFacilities } from "incidents/api";
import { typeLabel } from "incidents/display";
import { DismissalReport } from "incidents/types";
import { formatWhen } from "incidents/time";
import PrivateLayout from "layouts/PrivateLayout";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fieldSx, primaryButtonSx, SectionTitle, usePhrase } from "./common";

export default function DismissalsPage({ preview = false }: { preview?: boolean }) {
  const phrase = usePhrase();
  const locale = phrase("en", "fr") === "fr" ? "fr" : "en";
  const [month, setMonth] = useState(monthKey());
  const [facility, setFacility] = useState("all");
  const [homes, setHomes] = useState<{ id: string; name: string }[]>([]);
  const [report, setReport] = useState<DismissalReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const base = preview ? "/dev/incidents" : "/incidents";

  useEffect(() => {
    incidentsApi
      .list()
      .then((rows) => setHomes(visibleFacilities(rows)))
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    incidentsApi
      .dismissalReport(month, facility)
      .then((next) => {
        setReport(next);
        setError(null);
      })
      .catch((err: Error) => setError(err.message));
  }, [month, facility]);

  const body = (
    <Box>
      <Button component={Link} to={base} sx={{ ...primaryButtonSx, mb: 2 }}>
        {phrase("Back to incidents", "Retour aux incidents")}
      </Button>
      <Typography component="h1" sx={{ fontSize: 28, fontWeight: 700 }}>
        {phrase("Monthly dismissal report", "Rapport mensuel des rejets")}
      </Typography>
      <SectionTitle
        title={phrase("Counts by reason, type, and room", "Comptes par raison, type et chambre")}
        hint={phrase(
          "Dismissed Responsive Behaviour / Abuse alerts are highlighted. This report does not go to a regulator by itself.",
          "Les rejets de comportement réactif / maltraitance sont mis en évidence. Ce rapport n'est pas envoyé à un organisme."
        )}
      />
      <Box display="flex" gap={1.5} flexWrap="wrap" mb={2}>
        <TextField
          type="month"
          label={phrase("Month", "Mois")}
          value={month}
          onChange={(event) => setMonth(event.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={fieldSx}
        />
        <TextField
          select
          label={phrase("Home", "Milieu")}
          value={facility}
          onChange={(event) => setFacility(event.target.value)}
          sx={{ ...fieldSx, minWidth: 240 }}
        >
          <MenuItem value="all">{phrase("All homes", "Tous les milieux")}</MenuItem>
          {homes.map((home) => (
            <MenuItem key={home.id} value={home.id}>
              {home.name}
            </MenuItem>
          ))}
        </TextField>
      </Box>
      {error ? (
        <Typography role="alert" color="error">
          {error}
        </Typography>
      ) : null}
      {report ? (
        <Box display="grid" gap={2}>
          <Typography>
            {phrase("Dismissals", "Rejets")}: {report.total}.{" "}
            {phrase("Responsive Behaviour / Abuse", "Comportement réactif / maltraitance")}:{" "}
            {report.unsafe_interaction_dismissed}. {phrase("Still waiting", "Encore en attente")}:{" "}
            {report.pending_confirmation}.
          </Typography>
          <Typography>
            {phrase("By reason", "Par raison")}:{" "}
            {report.by_reason.map((item) => `${item.reason.replace(/_/g, " ")} ${item.count}`).join(", ") || "—"}
          </Typography>
          <Typography>
            {phrase("By room", "Par chambre")}:{" "}
            {report.by_room.map((item) => `${item.room} ${item.count}`).join(", ") || "—"}
          </Typography>
          <Box
            component="table"
            sx={{
              width: "100%",
              borderCollapse: "collapse",
              "& th, & td": { border: `1px solid ${COLORS.surface.border}`, p: 1, textAlign: "left" }
            }}
          >
            <thead>
              <tr>
                <th>{phrase("Record", "Dossier")}</th>
                <th>{phrase("Type", "Type")}</th>
                <th>{phrase("Room", "Chambre")}</th>
                <th>{phrase("Reason", "Raison")}</th>
                <th>{phrase("When", "Quand")}</th>
                <th>{phrase("Confirmer", "Confirmateur")}</th>
              </tr>
            </thead>
            <tbody>
              {report.rows.map((row) => (
                <tr key={row.id} style={{ background: row.unsafe_interaction ? "#FDECEE" : "transparent" }}>
                  <td>
                    <Link to={`${base}/${row.id}`}>{row.id}</Link>
                  </td>
                  <td>{typeLabel(row.type, locale)}</td>
                  <td>{row.room}</td>
                  <td>{row.reason.replace(/_/g, " ")}</td>
                  <td>{formatWhen(row.dismissed_at, locale === "fr" ? "fr-CA" : "en-CA")}</td>
                  <td>{row.confirmer || phrase("Waiting", "En attente")}</td>
                </tr>
              ))}
            </tbody>
          </Box>
        </Box>
      ) : null}
    </Box>
  );

  if (preview) return <Box sx={{ p: 2, bgcolor: COLORS.surface.page, minHeight: "100vh" }}>{body}</Box>;
  return <PrivateLayout canAccess={() => true}>{body}</PrivateLayout>;
}
